import i18n from "../i18n";import axios from "axios";
import { API_BASE_URL } from "./constant";
import { message } from "antd";
import { clearAllDrafts } from "../utils/draftStorage";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json"
  }
});

let refreshRequest = null;

function getStoredToken() {
  return localStorage.getItem("token") || localStorage.getItem("accessToken");
}

function storeSession(jwtData) {
  localStorage.setItem("token", jwtData.token);
  localStorage.setItem("accessToken", jwtData.token);
  if (jwtData.refreshToken) {
    localStorage.setItem("refreshToken", jwtData.refreshToken);
  }
  if (jwtData.email) localStorage.setItem("email", jwtData.email);
  if (jwtData.name) localStorage.setItem("name", jwtData.name);
  if (jwtData.id) localStorage.setItem("id", jwtData.id);
  if (jwtData.role) localStorage.setItem("role", jwtData.role);
}

function clearLocalSession() {
  const language = localStorage.getItem("i18nextLng");
  const appLanguage = localStorage.getItem("appLanguage");
  clearAllDrafts();
  localStorage.clear();
  if (language) localStorage.setItem("i18nextLng", language);
  if (appLanguage) localStorage.setItem("appLanguage", appLanguage);
}

function isAuthEndpoint(url = "") {
  return url.includes("/api/auth/login")
    || url.includes("/api/auth/register")
    || url.includes("/api/auth/refresh")
    || url.includes("/api/auth/logout");
}

function isTokenExpiringSoon(token) {
  if (!token) return false;
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, "=");
    const payload = JSON.parse(atob(padded));
    return payload.exp * 1000 <= Date.now() + 60000;
  } catch {
    return false;
  }
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  if (!refreshRequest) {
    refreshRequest = axios.post(`${API_BASE_URL}/api/auth/refresh`, { refreshToken }, {
      headers: { "Content-Type": "application/json" }
    }).then((response) => {
      const jwtData = response.data?.data;
      if (!jwtData?.token) return null;
      storeSession(jwtData);
      return jwtData.token;
    }).finally(() => {
      refreshRequest = null;
    });
  }

  return refreshRequest;
}

api.interceptors.request.use(
  async (config) => {
    let token = getStoredToken();

    if (!isAuthEndpoint(config.url) && isTokenExpiringSoon(token)) {
      token = await refreshAccessToken().catch(() => token);
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.config?.skipAuthRedirect) {
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      const originalRequest = error.config;
      if (!originalRequest?._retry && !isAuthEndpoint(originalRequest?.url)) {
        originalRequest._retry = true;
        const token = await refreshAccessToken().catch(() => null);
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      }

      message.destroy();
      message.error(i18n.t("Session expired or unauthorized. Please log in again."));
      clearLocalSession();
      setTimeout(() => {
        window.location.href = "/login";
      }, 1000);
    } else if (error.config?.suppressGlobalError) {
      return Promise.reject(error);
    } else if (error.response?.status === 403) {
      message.error(i18n.t("You don’t have permission to perform this action."));
    } else if (error.response?.status === 404) {
      message.error(i18n.t("Requested resource not found."));
    } else if (error.response?.status >= 500) {
      message.error(i18n.t("Server error. Please try again later."));
    }
    return Promise.reject(error);
  }
);

export default api;
